import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  Tag,
  Clock,
  Copy,
  ShoppingBag,
  Zap,
  Star,
  PackageOpen,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types (must mirror server/handlers/dealsHandlers.ts)
// ---------------------------------------------------------------------------

type DiscountType = "percentage" | "flat" | "free_delivery" | "other";

interface KudozBonusDeal {
  id: string;
  name: string;
  description: string | null;
  awardType: string;
  awardValue: string;
  conditions: Array<{ type: string; value: unknown }> | null;
  validFrom: string | null;
  validUntil: string | null;
}

interface RestaurantDeal {
  id: string;
  restaurantId: string;
  restaurantName: string;
  restaurantSlug: string;
  restaurantEatzyId: number | null;
  couponCode: string;
  discountType: DiscountType;
  discountValue: number | null;
  title: string;
  description: string | null;
  expiresAt: string | null;
  source: "eatzy_promo" | "quickpost";
}

interface ActiveDeals {
  kudozBonuses: KudozBonusDeal[];
  restaurantDeals: RestaurantDeal[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatExpiry(dateStr?: string | null): string | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  const diffDays = Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return "Expired";
  if (diffDays === 0) return "Expires today";
  if (diffDays === 1) return "Expires tomorrow";
  if (diffDays <= 7) return `${diffDays}d left`;
  return `Until ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function summarizeConditions(
  conditions: Array<{ type: string; value: unknown }> | null
): string | null {
  if (!conditions || conditions.length === 0) return null;
  const parts: string[] = [];

  for (const c of conditions) {
    switch (c.type) {
      case "day_of_week": {
        const raw = c.value;
        const days: number[] = Array.isArray(raw) ? (raw as number[]) : typeof raw === "number" ? [raw] : [];
        parts.push(days.map((d) => DAY_NAMES[d] ?? "?").join(" & "));
        break;
      }
      case "service_type": {
        const v = String(c.value);
        parts.push(v === "delivery" ? "Delivery only" : v === "takeout" ? "Takeout only" : v);
        break;
      }
      case "time_of_day": {
        if (typeof c.value === "object" && c.value !== null) {
          const t = c.value as { from?: string; to?: string };
          if (t.from && t.to) parts.push(`${t.from}–${t.to}`);
        }
        break;
      }
      case "min_order_amount":
        parts.push(`Min $${c.value}`);
        break;
      default:
        break;
    }
  }

  return parts.length > 0 ? parts.join(" · ") : null;
}

function kudozBadgeLabel(awardType: string, awardValue: string): string {
  const val = parseFloat(awardValue);
  if (awardType === "multiplier") return `${val}× Kudoz`;
  if (awardType === "flat_bonus") return `+${Math.round(val)} Kudoz`;
  return "Bonus Kudoz";
}

function discountBadgeLabel(discountType: DiscountType, discountValue: number | null): string {
  switch (discountType) {
    case "percentage":
      return discountValue !== null ? `${discountValue}% off` : "% off";
    case "flat":
      return discountValue !== null ? `$${discountValue} off` : "$ off";
    case "free_delivery":
      return "Free delivery";
    case "other":
    default:
      return "Special deal";
  }
}

// ---------------------------------------------------------------------------
// Skeleton helpers
// ---------------------------------------------------------------------------

function KudozBonusSkeleton() {
  return (
    <Card className="w-full">
      <CardContent className="p-4 space-y-3">
        <Skeleton className="h-5 w-24 rounded-full" />
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-3 w-32" />
      </CardContent>
    </Card>
  );
}

function RestaurantDealSkeleton() {
  return (
    <Card className="w-full">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-5 w-20 rounded-full" />
        </div>
        <Skeleton className="h-8 w-36 rounded-md" />
        <div className="flex items-center justify-between gap-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-8 w-24 rounded-md" />
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Kudoz Bonus Card
// ---------------------------------------------------------------------------

function KudozBonusCard({ deal }: { deal: KudozBonusDeal }) {
  const conditionText = summarizeConditions(deal.conditions);
  const expiry = formatExpiry(deal.validUntil);

  return (
    <Card className="w-full">
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className="bg-teal-600 text-white hover:bg-teal-600 shrink-0">
            <Zap className="w-3 h-3 mr-1" />
            {kudozBadgeLabel(deal.awardType, deal.awardValue)}
          </Badge>
          {expiry && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="w-3 h-3 shrink-0" />
              {expiry}
            </div>
          )}
        </div>
        <p className="text-sm font-semibold leading-snug">{deal.name}</p>
        {deal.description && (
          <p className="text-xs text-muted-foreground line-clamp-2">{deal.description}</p>
        )}
        {conditionText && (
          <p className="text-xs text-muted-foreground">{conditionText}</p>
        )}
        <p className="text-xs text-muted-foreground italic">
          Applies automatically at checkout
        </p>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Restaurant Deal Card
// ---------------------------------------------------------------------------

function RestaurantDealCard({ deal }: { deal: RestaurantDeal }) {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const expiry = formatExpiry(deal.expiresAt);
  const badgeLabel = discountBadgeLabel(deal.discountType, deal.discountValue);

  const handleCopy = () => {
    navigator.clipboard.writeText(deal.couponCode).then(() => {
      toast({
        title: "Copied!",
        description: `Code "${deal.couponCode}" copied to clipboard.`,
      });
    });
  };

  const handleOrder = () => {
    if (deal.restaurantId) {
      navigate(`/restaurant/${deal.restaurantId}`);
    } else if (deal.restaurantEatzyId) {
      navigate(`/r/${deal.restaurantEatzyId}`);
    }
  };

  return (
    <Card className="w-full">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <p className="text-sm font-semibold leading-snug flex-1 min-w-0 truncate">
            {deal.restaurantName}
          </p>
          <Badge variant="secondary" className="shrink-0 text-xs">
            {badgeLabel}
          </Badge>
        </div>

        {deal.description && deal.description !== deal.title && (
          <p className="text-xs text-muted-foreground">{deal.description}</p>
        )}

        {deal.title && (
          <p className="text-xs text-muted-foreground">{deal.title}</p>
        )}

        <div className="flex items-center gap-2">
          <code className="flex-1 min-w-0 truncate bg-muted rounded-md px-3 py-1.5 text-sm font-mono tracking-wider">
            {deal.couponCode}
          </code>
          <Button size="icon" variant="outline" onClick={handleCopy} aria-label="Copy coupon code">
            <Copy className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex items-center justify-between gap-2 flex-wrap">
          {expiry ? (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="w-3 h-3 shrink-0" />
              {expiry}
            </div>
          ) : (
            <span />
          )}
          <Button
            size="sm"
            variant="default"
            onClick={handleOrder}
            disabled={!deal.restaurantEatzyId && !deal.restaurantId}
          >
            <ShoppingBag className="w-3.5 h-3.5 mr-1.5" />
            Order Now
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptySection({
  icon: Icon,
  message,
}: {
  icon: React.ElementType;
  message: string;
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground text-sm text-center">
      <Icon className="w-8 h-8 opacity-30" />
      <p>{message}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Deals() {
  const { data, isLoading, error } = useQuery<ActiveDeals>({
    queryKey: ["/api/deals/active"],
    staleTime: 5 * 60_000,
  });

  const kudozBonuses = data?.kudozBonuses ?? [];
  const restaurantDeals = data?.restaurantDeals ?? [];

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <div className="sticky top-0 z-20 bg-background border-b px-4 py-4">
        <div className="flex items-center gap-2">
          <Tag className="w-5 h-5 text-primary shrink-0" />
          <div>
            <h1 className="text-lg font-bold leading-tight">Deals &amp; Offers</h1>
            <p className="text-xs text-muted-foreground">
              Limited-time savings and bonus Kudoz
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-8 pb-24">
        {error instanceof Error && (
          <p className="text-sm text-destructive text-center py-4">
            Failed to load deals. Please try again later.
          </p>
        )}

        <section>
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-teal-600" />
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Kudoz Bonuses
            </h2>
          </div>

          {isLoading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <KudozBonusSkeleton key={i} />
              ))}
            </div>
          ) : kudozBonuses.length === 0 ? (
            <EmptySection
              icon={Star}
              message="No active Kudoz bonuses right now. Check back soon!"
            />
          ) : (
            <div className="space-y-3">
              {kudozBonuses.map((deal) => (
                <KudozBonusCard key={deal.id} deal={deal} />
              ))}
            </div>
          )}
        </section>

        <section>
          <div className="flex items-center gap-2 mb-3">
            <Tag className="w-4 h-4 text-orange-500" />
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Restaurant Deals
            </h2>
          </div>

          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <RestaurantDealSkeleton key={i} />
              ))}
            </div>
          ) : restaurantDeals.length === 0 ? (
            <EmptySection
              icon={PackageOpen}
              message="No restaurant deals available right now. Check back soon!"
            />
          ) : (
            <div className="space-y-3">
              {restaurantDeals.map((deal) => (
                <RestaurantDealCard key={deal.id} deal={deal} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
